import type { FlexProps } from "@qws-org/ui-kit";
import {
  Box,
  Button,
  Flex,
  Image,
  Markdown,
  Title,
  Typography,
} from "@qws-org/ui-kit";
import type { FC } from "react";
import React from "react";

import type { Action, Event_Card, Pages } from "~/core";
import { isExternalLink, isInternalLink } from "~/core/utils/action.utils";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

type EventCardProps = {
  data: Event_Card;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  isActionInactive: (action: Action) => boolean;
} & FlexProps;

export const EventCard: FC<EventCardProps> = (props) => {
  const { data, getUrl, onCallAction, isActionInactive, ...rest } = props;
  const { getDirectusImage } = useDirectus();
  const controlAction = data.action;
  const action = controlAction?.action?.[0];
  let href;

  if (isExternalLink(action?.item)) {
    href = action.item.href ?? undefined;
  }

  if (isInternalLink(action?.item)) {
    href = action.item.page ? getUrl(action.item.page) : undefined;
  }

  return (
    <Flex
      flexDirection="column"
      p={{ px: 16, pb: 32 }}
      border={{ radius: "big-card" }}
      bg="surface-neutral-default"
      width="100%"
      {...rest}
      md={{
        p: { px: 48, pb: 48 },
        flexDirection: "row",
        gap: 20,
        ...rest.md,
      }}
    >
      <Flex
        width="100%"
        flexDirection="column"
        md={{ gap: 20, flexDirection: "row" }}
      >
        <Box width="100%" md={{ width: "50%" }}>
          {data.logo?.id && (
            <Image
              maxWidth={116}
              m={{ mt: -24, mb: 20, mx: "auto" }}
              md={{ m: { mb: 32, mx: 0 }, maxWidth: 160 }}
              display="block"
              value={getDirectusImage(data.logo?.id, {
                format: "webp",
                width: 116,
              })}
              mdValue={getDirectusImage(data.logo?.id, {
                format: "webp",
                width: 160,
              })}
            />
          )}
          <Title
            maxWidth={452}
            as="h2"
            fontWeight="mobile.bold"
            fontSize="title.mobile.large"
            lineHeight="title.mobile.large"
            horizontalAlign="center"
            m={{ mb: 32 }}
            md={{
              m: { mb: 51 },
              horizontalAlign: "left",
              fontWeight: "desktop.bold",
              fontSize: "title.desktop.large",
              lineHeight: "title.desktop.large",
            }}
          >
            {data.title}
          </Title>
          {controlAction &&
          !isActionInactive(controlAction) &&
          data?.action_text ? (
            <Button
              href={href}
              fontWeight="desktop.bold"
              fontSize="button.desktop.medium"
              lineHeight="button.desktop.medium"
              onPress={() => {
                if (controlAction) {
                  onCallAction(controlAction);
                }
              }}
              display="hidden"
              md={{ display: "block", p: { px: 48, py: 17 } }}
              maxWidth="fit-content"
              p={{ px: 48, py: 17 }}
              className="text-center"
            >
              {data.action_text}
            </Button>
          ) : null}
        </Box>
        <Box md={{ width: "50%", p: { pt: 40 } }}>
          <Flex align="center" gap={16} m={{ mb: 13 }}>
            {data.place_icon?.id && (
              <Image
                width={32}
                value={getDirectusImage(data.place_icon?.id, {
                  format: "webp",
                  width: 32,
                })}
              />
            )}
            <Typography
              fontWeight="mobile.normal"
              fontSize="body.mobile.medium"
              lineHeight="body.mobile.medium"
              md={{
                fontSize: "body.desktop.medium",
                lineHeight: "body.desktop.medium",
                fontWeight: "desktop.normal",
              }}
            >
              {data.place_text}
            </Typography>
          </Flex>
          {data.time && (
            <Flex m={{ mb: 13 }} align="center" gap={16}>
              {data.time_icon?.id && (
                <Image
                  width={32}
                  value={getDirectusImage(data.time_icon?.id, {
                    format: "webp",
                    width: 32,
                  })}
                />
              )}
              <Typography
                fontWeight="mobile.normal"
                fontSize="body.mobile.medium"
                lineHeight="body.mobile.medium"
                md={{
                  fontSize: "body.desktop.medium",
                  lineHeight: "body.desktop.medium",
                  fontWeight: "desktop.normal",
                }}
              >
                {data.time}
              </Typography>
            </Flex>
          )}
          {data.reward && (
            <Flex m={{ mb: 13 }} align="center" gap={16}>
              {data.reward_icon?.id && (
                <Image
                  width={32}
                  value={getDirectusImage(data.reward_icon?.id, {
                    format: "webp",
                    width: 32,
                  })}
                />
              )}
              <Typography
                fontWeight="mobile.normal"
                fontSize="body.mobile.medium"
                lineHeight="body.mobile.medium"
                md={{
                  fontSize: "body.desktop.medium",
                  lineHeight: "body.desktop.medium",
                  fontWeight: "desktop.normal",
                }}
              >
                {data.reward}
              </Typography>
            </Flex>
          )}
          {data.partner_info && (
            <Flex m={{ mb: 32 }} md={{ m: { mb: 48 } }} align="center" gap={16}>
              {data.partner_info_icon?.id && (
                <Image
                  width={32}
                  value={getDirectusImage(data.partner_info_icon?.id, {
                    format: "webp",
                    width: 32,
                  })}
                />
              )}
              <Typography
                fontWeight="mobile.normal"
                fontSize="body.mobile.medium"
                lineHeight="body.mobile.medium"
                md={{
                  fontSize: "body.desktop.medium",
                  lineHeight: "body.desktop.medium",
                  fontWeight: "desktop.normal",
                }}
              >
                {data.partner_info}
              </Typography>
            </Flex>
          )}
          {data.text && (
            <Markdown m={{ mb: 32 }} md={{ m: { mb: 0 } }}>
              {data.text}
            </Markdown>
          )}
          {controlAction && !isActionInactive(controlAction) ? (
            <Button
              href={href}
              fontSize="button.mobile.small"
              lineHeight="button.mobile.small"
              md={{ display: "hidden" }}
              fontWeight="mobile.bold"
              onPress={() => {
                if (controlAction) {
                  onCallAction(controlAction);
                }
              }}
              p={{ px: 24, py: 12 }}
              m={{ mx: "auto" }}
              display="block"
              className="text-center"
              maxWidth="fit-content"
            >
              {data.action_text}
            </Button>
          ) : null}
        </Box>
      </Flex>
    </Flex>
  );
};
